import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { usePrefs } from '@/app/prefs';
import { Button, Icon } from '@/components/ui';

interface TopbarProps {
  /** Current screen name, shown after the breadcrumb root. */
  title: ReactNode;
  user?: { name: string; roleLabel: string; initials: string };
  hasNotifications?: boolean;
  onToggleNav: () => void;
  onLogout?: () => void;
}

export function Topbar({ title, user, hasNotifications, onToggleNav, onLogout }: TopbarProps) {
  const { t, mode, toggleLang, toggleMode } = usePrefs();
  const searchRef = useRef<HTMLInputElement>(null);
  const userboxRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // ⌘K / Ctrl+K focuses search from anywhere. The command palette itself is a
  // later phase; this is the shell it will hang off.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Closes the profile menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!userboxRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="sn-topbar">
      <Button
        size="icon"
        className="sn-burger"
        icon="menu"
        onClick={onToggleNav}
        aria-label={t.menuMain}
      />

      <div className="sn-crumb">
        <span>{t.breadcrumbRoot}</span>
        <span aria-hidden="true">›</span>
        <span className="sn-crumb-current">{title}</span>
      </div>

      <div className="sn-search">
        <Icon name="search" size={15} className="sn-search-icon" />
        <input
          ref={searchRef}
          className="input"
          type="search"
          placeholder={t.searchPlaceholder}
          aria-label={t.searchPlaceholder}
        />
        <span className="sn-kbd" aria-hidden="true">
          ⌘K
        </span>
      </div>

      <div className="sn-topbar-actions">
        <Button className="sn-lang-btn" onClick={toggleLang} aria-label={t.settings.language}>
          {t.langLabel}
        </Button>

        <Button
          size="icon"
          icon={mode === 'dark' ? 'sun' : 'moon'}
          onClick={toggleMode}
          aria-label={mode === 'dark' ? t.modeLight : t.modeDark}
        />

        <Button size="icon" className="sn-bell" icon="bell" aria-label={t.activityTitle}>
          {hasNotifications && <span className="sn-bell-dot" />}
        </Button>

        {user && (
          <div className="sn-userbox" ref={userboxRef}>
            <button
              type="button"
              className="sn-userbox-trigger"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <div className="sn-avatar" aria-hidden="true">
                {user.initials}
              </div>
              <div className="sn-username">
                <div className="sn-username-name">{user.name}</div>
                <div className="sn-username-role">{user.roleLabel}</div>
              </div>
            </button>

            {menuOpen && (
              <div className="sn-userbox-panel" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="sn-userbox-menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout?.();
                  }}
                >
                  <Icon name="logout" />
                  <span>{t.btn.logout}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
